import {
  Box,
  Flex,
  HStack,
  Icon,
  Select,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import {
  UseFieldArrayAppend,
  UseFieldArrayMove,
  UseFieldArrayRemove,
} from "react-hook-form";
import { HiOutlinePlus } from "react-icons/hi";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { v4 as uuidv4 } from "uuid";

import { Button } from "../../../components";
import {
  AiInterviewQuestion,
  AiInterviewQuestionType,
  GuideAnalysisResult,
} from "../../graphql";
import AiInterviewerGuideQuestionItem from "./AiInterviewerGuideQuestionItem";
import {
  QuestionSuggestionPreviewState,
  SuggestionFieldTarget,
} from "./GuideAssistantV2/types";
import { AiInterviewerFormValues } from "./types";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";

type AiInterviewerGuideQuestionsProps = {
  readOnly?: boolean;
  questions: AiInterviewQuestion[];
  appendQuestion: UseFieldArrayAppend<AiInterviewerFormValues, "questions">;
  removeQuestion: UseFieldArrayRemove;
  move: UseFieldArrayMove;
  analysisResult?: GuideAnalysisResult | null;
  reservePanelSpace?: boolean;
  highlightedQuestionId?: string | null;
  previewsByQuestionId?: Record<
    string,
    (QuestionSuggestionPreviewState & { questionIndex: number }) | null
  >;
  applyingSuggestionId?: string | null;
  ignoringSuggestionId?: string | null;
  onResolvePreviewField?: (
    questionId: string,
    fallbackQuestionIndex: number,
    target: SuggestionFieldTarget,
    decision: "accept" | "reject"
  ) => void;
  onResolvePreviewAll?: (
    questionId: string,
    fallbackQuestionIndex: number
  ) => void;
  onIgnorePreview?: (questionId: string, fallbackQuestionIndex: number) => void;
};

const AiInterviewerGuideQuestions: React.FC<
  AiInterviewerGuideQuestionsProps
> = ({
  readOnly,
  questions,
  appendQuestion,
  removeQuestion,
  move,
  analysisResult,
  reservePanelSpace,
  highlightedQuestionId,
  previewsByQuestionId,
  applyingSuggestionId,
  ignoringSuggestionId,
  onResolvePreviewField,
  onResolvePreviewAll,
  onIgnorePreview,
}) => {
  // Track which questions have had their suggestions accepted (by ID, not index)
  const [collapsedQuestionIds, setCollapsedQuestionIds] = useState<Set<string>>(
    new Set()
  );

  const handleSuggestionsAccepted = useCallback((questionId: string) => {
    setCollapsedQuestionIds((prev) => new Set(prev).add(questionId));
  }, []);

  const onDragEnd = (result: DropResult): void => {
    const { destination, source } = result;
    if (!destination) return;
    if (destination.index === source.index) return;
    move(source.index, destination.index);
  };

  const { register, watch, setValue } = useAiInterviewerFormContext();
  const canEnableQuestionWeights =
    !readOnly &&
    watch("questions").filter((q) => q.type === AiInterviewQuestionType.Score)
      .length > 1;

  useEffect(() => {
    if (!canEnableQuestionWeights) {
      setValue("customQuestionWeightsEnabled", false);
    }
  }, [canEnableQuestionWeights]);

  const handleAddQuestion = (): void => {
    const questionId = uuidv4();
    appendQuestion({
      id: questionId,
      originalQuestionId: questionId,
      question: `New question ${questions.length + 1}`,
      followups: [],
      type: AiInterviewQuestionType.Summary,
      rubric: [],
      qualificationCriteria: "",
      summaryCriteria: "",
      sortOrder: questions.length,
    });
  };

  return (
    <Box w="100%">
      {!readOnly && (
        <Flex
          justify="space-between"
          align="center"
          gap="2"
          minH="10"
          flexWrap="wrap"
          w="100%"
          minW={0}
        >
          <Button
            size="sm"
            onClick={handleAddQuestion}
            data-testid="add-question-top-btn"
            variant="prismTertiary"
            minWidth="120px"
            flexShrink={0}
            leftIcon={<HiOutlinePlus />}
            isDisabled={readOnly}
          >
            Add question
          </Button>

          {canEnableQuestionWeights && (
            <Flex align="center" gap="2" minW={0} flexWrap="wrap">
              <Tooltip
                label={
                  <>
                    After the interview, the candidate will be scored based on
                    their responses to any &quot;Numeric score&quot; questions.
                    The weighting of these questions is equal by default, but
                    may be changed here. The sum of all score weightings must
                    equal 100%.
                  </>
                }
                openDelay={300}
              >
                <Flex
                  align="center"
                  gap="1"
                  color="gray.600"
                  fontSize="sm"
                  whiteSpace="nowrap"
                >
                  Score weighting
                  <Icon
                    as={HiOutlineInformationCircle}
                    boxSize="4"
                    color="gray.500"
                  />
                </Flex>
              </Tooltip>
              <Select
                w="96px"
                size="sm"
                borderRadius="6px"
                {...register("customQuestionWeightsEnabled", {
                  setValueAs: (value) => value.toString() === "true",
                })}
              >
                <option value="false">Equal</option>
                <option value="true">Custom</option>
              </Select>
            </Flex>
          )}
        </Flex>
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="questions-list">
          {(provided, snapshot) => (
            <Flex
              direction="column"
              ref={provided.innerRef}
              {...provided.droppableProps}
              minH="60px"
              bg={snapshot.isDraggingOver ? "gray.50" : undefined}
              borderRadius="md"
              p={2}
              pt={0}
              gap={2}
              mt={2}
              w="100%"
            >
              {questions.length === 0 && (
                <Text color="gray.400" fontSize="sm" textAlign="center">
                  No questions yet.
                </Text>
              )}
              {questions.map((q, idx) => {
                // Match by text (stable across drag-and-drop reorder).
                // Use questionIndex as tiebreaker for duplicate question text.
                const questionAnalysis = collapsedQuestionIds.has(q.id)
                  ? null
                  : analysisResult?.questionAnalyses.find(
                      (a) =>
                        a.questionText === q.question && a.questionIndex === idx
                    ) ??
                    analysisResult?.questionAnalyses.find(
                      (a) => a.questionText === q.question
                    ) ??
                    null;
                return (
                  <Draggable
                    draggableId={q.id}
                    index={idx}
                    key={q.id}
                    isDragDisabled={readOnly}
                  >
                    {(provided, snapshot) => (
                      <HStack
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        w="100%"
                        minW={0}
                      >
                        <AiInterviewerGuideQuestionItem
                          index={idx}
                          questionId={q.id}
                          dragHandleProps={provided.dragHandleProps}
                          isDragging={snapshot.isDragging}
                          onDelete={() => removeQuestion(idx)}
                          readOnly={readOnly}
                          canEnableQuestionWeights={canEnableQuestionWeights}
                          questionAnalysis={questionAnalysis}
                          onSuggestionsAccepted={() =>
                            handleSuggestionsAccepted(q.id)
                          }
                          reservePanelSpace={reservePanelSpace}
                          highlighted={highlightedQuestionId === q.id}
                          assistantPreview={
                            previewsByQuestionId
                              ? previewsByQuestionId[q.id] ??
                                previewsByQuestionId[`index:${idx}`]
                              : null
                          }
                          applyingSuggestionId={applyingSuggestionId}
                          ignoringSuggestionId={ignoringSuggestionId}
                          onResolveAssistantPreviewField={onResolvePreviewField}
                          onResolveAssistantPreviewAll={onResolvePreviewAll}
                          onIgnoreAssistantPreview={onIgnorePreview}
                        />
                      </HStack>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </Flex>
          )}
        </Droppable>
      </DragDropContext>
      {!readOnly && (
        <Button
          size="sm"
          onClick={handleAddQuestion}
          data-testid="add-question-bottom-btn"
          variant="prismTertiary"
          minWidth="120px"
          leftIcon={<HiOutlinePlus />}
          isDisabled={readOnly}
        >
          Add question
        </Button>
      )}
    </Box>
  );
};

export default AiInterviewerGuideQuestions;
